import { useState } from 'react'
import { Modal } from 'react-bootstrap'
import { upgradeSubscription } from '@healthshare/shared/commands/billing'
import { useBackendApi } from '../hooks/useBackendApi'
import { useAppDispatch, useAppSelector } from '../hooks/reduxToolkit'
import { useClient } from '../providers/supabase'
import { useSentry } from '../providers/sentry'
import { setSubscription } from '../reduxToolkit/membershipSlice'

interface UpgradePlanModalProps {
  show: boolean
  onHide: () => void
  title?: string
  message?: string
  /** Called after a successful upgrade, once the Redux subscription is refreshed. */
  onUpgraded?: () => void
}

/**
 * Prompts a Solo subscriber to upgrade to the Family plan in place — the
 * backend swaps the Stripe subscription price with proration, no checkout
 * redirect. Shown when a Solo action requires Family (submitting for a second
 * person, inviting a spouse); see lib/rules/requiredPlan.ts.
 */
export function UpgradePlanModal({
  show,
  onHide,
  title = 'Family Plan Required',
  message = 'Your Solo plan covers submissions for yourself. Upgrade to the Family plan to cover everyone in your household.',
  onUpgraded,
}: UpgradePlanModalProps) {
  const api = useBackendApi()
  const { captureException } = useSentry()
  const client = useClient()
  const dispatch = useAppDispatch()
  const membershipId = useAppSelector((s) => s.membership?.membershipId)

  const [upgrading, setUpgrading] = useState(false)
  const [upgradeError, setUpgradeError] = useState<string>()

  const handleUpgrade = async () => {
    setUpgrading(true)
    setUpgradeError(undefined)

    try {
      await upgradeSubscription(api)

      // Refresh the Redux subscription so the tier change takes effect
      // immediately, without waiting for the next sync.
      if (membershipId) {
        const { data, error } = await client
          .from('subscriptions')
          .select('*')
          .eq('membership_id', membershipId)
          .maybeSingle()
        if (!error && data) {
          dispatch(setSubscription({ subscription: data }))
        }
      }

      onUpgraded?.()
      onHide()
    } catch (err) {
      captureException(err, { tags: { feature: 'subscription', subscription_op: 'upgrade' } })
      setUpgradeError(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setUpgrading(false)
    }
  }

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>{message}</p>
        <p className="text-muted small mb-0">
          Your subscription switches to the Family plan right away, and the price
          difference is prorated on your next invoice.
        </p>
        {upgradeError && (
          <div className="alert alert-danger mt-3 mb-0">{upgradeError}</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-link" onClick={onHide} disabled={upgrading}>
          Not now
        </button>
        <button
          className={`btn btn-primary ${upgrading ? 'disabled' : ''}`}
          onClick={handleUpgrade}
          disabled={upgrading}
        >
          {upgrading && (
            <span className="spinner-border spinner-border-sm me-2" role="status">
              <span className="visually-hidden">Loading...</span>
            </span>
          )}
          Upgrade to Family Plan
        </button>
      </Modal.Footer>
    </Modal>
  )
}
